"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import { transformProperties } from "@/utils/simplifyData"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import {
  transitionDelayTime,
  useInViewConfig,
  useIsDesktop,
  useTransitionUpStyles,
} from "ui"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import CommonTitle from "ui/common/bit/CommonTitle"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import { colors } from "ui/theme/colors"
import { mediaQueries } from "ui/theme/mediaQueries"
import { getNonUmbracoUrl } from "utils"
import { sendBlockEvent } from "utils/gtm"
import BlockWrapper from "../common/BlockWrapper"

const StyledMoreBoxesContainer = styled(Box)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  ${mediaQueries.desktopM} {
    flex-direction: row;
  }
  ${mediaQueries.desktopXl} {
    gap: 30px;
  }
`

const StyledMoreBox = styled(Box)`
  background-color: ${colors.white};
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  padding: 20px 16px;
  border-radius: 8px;
  box-shadow: ${colors.dropShadow.main};
  text-decoration: none;
  svg {
    width: 26px;
  }
  ${mediaQueries.desktopM} {
    justify-content: normal;
    flex-direction: column;
    width: calc(100% / 3 - 30px);
    max-width: 375px;
    max-height: 416px;
    border-radius: 30px;
    padding: 40px 50px;
  }

  ${mediaQueries.desktopXl} {
    width: 500px;
    height: 500px;
    max-width: 500px;
    max-height: 500px;
    padding: 40px 60px;
  }
`

const StyledImageContainer = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 194px;
  height: 161px;
  margin-bottom: 40px;
  img {
    object-fit: contain;
  }
  ${mediaQueries.tabletDown} {
    display: none;
  }
  ${mediaQueries.desktopXl} {
    width: 286px;
    height: 238px;
  }
`

const StyledMoreBoxTitle = styled(Typography)<{ component?: string }>`
  margin-bottom: 10px;
  color: ${colors.black};
  text-align: start;
  ${mediaQueries.desktopM} {
    text-align: center;
  }
`

const StyledMoreBoxDescription = styled(StyledMoreBoxTitle)<{
  component?: string
}>`
  font-weight: 400;
  margin-bottom: 0;
  ${mediaQueries.desktopM} {
    margin-bottom: 20px;
  }
`

const StyledTextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: auto;
  ${mediaQueries.desktopM} {
    align-items: center;
  }
`

const StyledMoreBoxButton = styled(ButtonWithArrow)`
  display: flex;
  ${mediaQueries.tabletDown} {
    display: none;
  }
  ${mediaQueries.desktopM} {
    font-size: 16px;
    white-space: nowrap;
  }
`

const MoreComponent = ({ id, data }: any) => {
  const transformedData = data
  const isDesktop = useIsDesktop()
  const { ref, inView } = useInView(useInViewConfig)
  const { title, subtitle } = transformedData
  const ditransitiveStyles = useTransitionUpStyles(inView, transitionDelayTime)
  return (
    <BlockWrapper id={id}>
      <CommonTitle title={title.value} subtitle={subtitle.value} />
      <StyledMoreBoxesContainer ref={ref} style={{ ...ditransitiveStyles }}>
        {transformedData.itemsList.blocks.map((box: any, index: number) => {
          const ArrowIcon = iconsMap["arrow_lightOrange_black_line"]
          const { desktopImage, title, text, button } = transformProperties(
            box?.properties,
          )
          return (
            <StyledMoreBox
              key={`MoreComponent-StyledMoreBox-${index}`}
              {...(!isDesktop && button
                ? {
                    component: "a",
                    href: getNonUmbracoUrl(button?.links[0]?.url),
                  }
                : {})}
            >
              {desktopImage && isDesktop && (
                <StyledImageContainer>
                  {isLottieFile(desktopImage.mediaItems[0]?.url) ? (
                    <LottiePlayer
                      lottieUrl={desktopImage.mediaItems[0]?.url}
                      playOnce={true}
                      autoplay={true}
                      loop={false}
                      ariaLabel={title?.value}
                    />
                  ) : (
                    <Image
                      fill
                      src={desktopImage?.mediaItems[0]?.url}
                      alt={title?.value}
                    />
                  )}
                </StyledImageContainer>
              )}
              <StyledTextContainer>
                {title?.value && (
                  <StyledMoreBoxTitle variant="h4" component="h3">
                    {title.value}
                  </StyledMoreBoxTitle>
                )}
                {text?.value && (
                  <StyledMoreBoxDescription variant="h5" component="p">
                    {text?.value}
                  </StyledMoreBoxDescription>
                )}
                {isDesktop && button && (
                  <StyledMoreBoxButton
                    href={getNonUmbracoUrl(button?.links[0]?.url)}
                    onClick={() => {
                      sendBlockEvent({
                        blockId: "5",
                        componentName: "עוד מבית הכשרה",
                        title: title?.value,
                        buttonName: button?.links[0]?.name,
                        productName: data?.formDefaultInsuranceType,
                      })
                    }}
                  >
                    {button?.links[0]?.name}
                  </StyledMoreBoxButton>
                )}
              </StyledTextContainer>
              {!isDesktop && <ArrowIcon />}
            </StyledMoreBox>
          )
        })}
      </StyledMoreBoxesContainer>
    </BlockWrapper>
  )
}

export default MoreComponent
